/**
 * R2-1/R2-2 — pure step-funnel derivation from `data.screenFlowEdges`
 * (ux-rework-plan.md §6 Phase R2; approved mock overview-version-scope.html).
 *
 * The hero funnel follows the DOMINANT forward path: step 1 is the funnel
 * entry, each next step is the strongest outgoing edge to an unvisited screen.
 * Counts are data-true, never modeled:
 * - step 1 `entered` = scope `startedJourneys` (fallback: the entry screen's
 *   total outgoing unique journeys when the summary is absent);
 * - step N≥2 `entered` = uniqueJourneys on the chosen main-path edge — i.e.
 *   «reached from the previous step», exactly what the approved mock shows.
 * React-free and import.meta-free so jest and Playwright can import it.
 */

export interface FunnelStepEdgeLike {
  fromScreenId?: string;
  fromScreenName?: string;
  toScreenId?: string;
  toScreenName?: string;
  transitionCount: number;
  uniqueJourneys: number;
}

export interface FunnelStep {
  screenId: string;
  label: string;
  /** Journeys reaching this step (see derivation note above). */
  entered: number;
  /** entered − next.entered; 0 for the last step. */
  droppedAfter: number;
  /** entered / first step entered (null when the first step has 0). */
  shareOfFirst: number | null;
  /** entered / previous step entered (null for the first step). */
  stepConversion: number | null;
}

export interface FunnelStepExit {
  label: string;
  toScreenId?: string;
  count: number;
  /** count / step entered (null when entered is 0). */
  share: number | null;
  /** 'screen' = observed transition; 'left' = no further screen recorded. */
  kind: 'screen' | 'left';
}

const MAX_STEPS = 12;

function screenLabel(edge: FunnelStepEdgeLike, side: 'from' | 'to'): string {
  return side === 'from'
    ? edge.fromScreenName || edge.fromScreenId || 'Start'
    : edge.toScreenName || edge.toScreenId || 'Exit';
}

function usableEdges(edges: FunnelStepEdgeLike[]): FunnelStepEdgeLike[] {
  return edges.filter(edge => edge.fromScreenId && edge.toScreenId && edge.fromScreenId !== edge.toScreenId);
}

/** Entry screen: appears as `from` but never as `to`; ties → most outgoing journeys. */
function findEntryScreen(edges: FunnelStepEdgeLike[]): string | null {
  const outgoing = new Map<string, number>();
  const incoming = new Set<string>();
  for (const edge of edges) {
    outgoing.set(edge.fromScreenId!, (outgoing.get(edge.fromScreenId!) ?? 0) + edge.uniqueJourneys);
    incoming.add(edge.toScreenId!);
  }
  const roots = [...outgoing.entries()].filter(([id]) => !incoming.has(id));
  const pool = roots.length > 0 ? roots : [...outgoing.entries()];
  pool.sort((a, b) => b[1] - a[1]);
  return pool[0]?.[0] ?? null;
}

export function deriveFunnelSteps(
  rawEdges: FunnelStepEdgeLike[],
  startedJourneys?: number | null,
): FunnelStep[] {
  const edges = usableEdges(rawEdges);
  if (edges.length === 0) return [];
  const entryId = findEntryScreen(edges);
  if (!entryId) return [];

  const labelByScreen = new Map<string, string>();
  for (const edge of edges) {
    if (!labelByScreen.has(edge.fromScreenId!)) labelByScreen.set(edge.fromScreenId!, screenLabel(edge, 'from'));
    if (!labelByScreen.has(edge.toScreenId!)) labelByScreen.set(edge.toScreenId!, screenLabel(edge, 'to'));
  }

  const visited = new Set<string>([entryId]);
  const path: Array<{ screenId: string; entered: number }> = [];
  const entryOutgoing = edges
    .filter(edge => edge.fromScreenId === entryId)
    .reduce((sum, edge) => sum + edge.uniqueJourneys, 0);
  path.push({
    screenId: entryId,
    entered: startedJourneys && startedJourneys > 0 ? startedJourneys : entryOutgoing,
  });

  let current = entryId;
  while (path.length < MAX_STEPS) {
    const candidates = edges
      .filter(edge => edge.fromScreenId === current && !visited.has(edge.toScreenId!))
      .sort((a, b) => b.uniqueJourneys - a.uniqueJourneys || b.transitionCount - a.transitionCount);
    const next = candidates[0];
    if (!next) break;
    visited.add(next.toScreenId!);
    path.push({ screenId: next.toScreenId!, entered: next.uniqueJourneys });
    current = next.toScreenId!;
  }

  const first = path[0]?.entered ?? 0;
  return path.map((step, index) => {
    const nextEntered = path[index + 1]?.entered;
    const previousEntered = path[index - 1]?.entered;
    return {
      screenId: step.screenId,
      label: labelByScreen.get(step.screenId) ?? step.screenId,
      entered: step.entered,
      droppedAfter: nextEntered === undefined ? 0 : Math.max(0, step.entered - nextEntered),
      shareOfFirst: first > 0 ? step.entered / first : null,
      stepConversion: index === 0 || !previousEntered ? null : step.entered / previousEntered,
    };
  });
}

/**
 * Exit destinations for a selected step: every observed outgoing transition
 * plus the «left the funnel» remainder (entered − Σ outgoing unique) when the
 * step's arrivals exceed its recorded departures.
 */
export function deriveStepExits(
  rawEdges: FunnelStepEdgeLike[],
  steps: FunnelStep[],
  screenId: string,
  limit = 5,
): FunnelStepExit[] {
  const step = steps.find(entry => entry.screenId === screenId);
  if (!step) return [];
  const outgoing = usableEdges(rawEdges)
    .filter(edge => edge.fromScreenId === screenId)
    .sort((a, b) => b.uniqueJourneys - a.uniqueJourneys);
  const share = (count: number): number | null => (step.entered > 0 ? count / step.entered : null);
  const exits: FunnelStepExit[] = outgoing.slice(0, limit).map(edge => ({
    label: screenLabel(edge, 'to'),
    toScreenId: edge.toScreenId,
    count: edge.uniqueJourneys,
    share: share(edge.uniqueJourneys),
    kind: 'screen' as const,
  }));
  const departed = outgoing.reduce((sum, edge) => sum + edge.uniqueJourneys, 0);
  const left = step.entered - departed;
  if (left > 0) {
    exits.push({ label: 'Left the funnel (no further screen)', count: left, share: share(left), kind: 'left' });
    exits.sort((a, b) => b.count - a.count);
  }
  return exits.slice(0, limit);
}
